import React, { useEffect, useMemo, useState } from 'react';
import { Table, Button, Modal, Input, Select, Switch, Toast, Tag, Pagination } from '@douyinfe/semi-ui';
import { IconPlus, IconUpload } from '@douyinfe/semi-icons';
import { API, apiErrMsg } from '../helpers';
import { usePagedRows } from '../helpers/usePagedRows';

// 风控词库管理
//
// 这个页面对应《服务使用知情确认书》v2 第 4 条承诺的「关键词过滤 + 违规内容拦截」。
// 词库改动**即时生效**(后端有 30 秒 TTL 缓存,管理端改完主动失效),不用重启服务。
//
// 处置动作:
//   block  命中即拦截(默认)—— 请求直接被拒,不扣费、不打上游
//   review 只记录不拦截 —— 灰度观察用:看命中量与误伤率,确认词库准了再转 block

const EMPTY_FORM = {
  word: '',
  category: '其他',
  action: 'block',
  remark: '',
  enabled: true,
};

// 常用分类(可自由输入,这里只是快捷选项)
const CATEGORY_OPTIONS = ['诈骗', '赌博', '色情', '暴力', '违禁品', '黑客攻击', '侵权', '其他'];

const AdminRisk = () => {
  const [list, setList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [bulkText, setBulkText] = useState('');
  const [bulkCategory, setBulkCategory] = useState('其他');
  const [saving, setSaving] = useState(false);
  const [filterCat, setFilterCat] = useState('');
  const [filterWord, setFilterWord] = useState('');

  // 风控总开关与默认动作
  const [cfg, setCfg] = useState({ risk_filter_enabled: '1', risk_filter_action: 'block' });

  const TABLE_PAGE_SIZE = 20;

  // 搜索/分类筛选:在**前端**过滤。
  //
  // 为什么不在后端筛:词库是全量拉下来的(量很小),
  // 每敲一个字就发一次请求反而更慢、还会在输入框里卡顿。
  // ⚠ 之前这两个输入框只是改了 state、并没有重新查库 —— 等于按了没反应。
  const filtered = useMemo(() => {
    const kw = filterWord.trim().toLowerCase();
    return (list || []).filter((r) => {
      if (filterCat && r.category !== filterCat) return false;
      if (kw && !String(r.word || '').toLowerCase().includes(kw)) return false;
      return true;
    });
  }, [list, filterCat, filterWord]);

  const { page: tablePage, pagedList, setPage: setTablePage } = usePagedRows(filtered, TABLE_PAGE_SIZE);

  const load = () => {
    setLoading(true);
    API.get('/api/admin/risk/keywords')
      .then((r) => {
        setList(r.data.data?.items || []);
        setCategories(r.data.data?.categories || []);
      })
      .catch((e) => Toast.error(apiErrMsg(e)))
      .finally(() => setLoading(false));
    API.get('/api/admin/settings')
      .then((r) => {
        const d = r.data.data || {};
        setCfg({
          risk_filter_enabled: String(d.risk_filter_enabled ?? '1'),
          risk_filter_action: String(d.risk_filter_action ?? 'block'),
        });
      })
      .catch(() => {});
  };
  useEffect(load, []);

  // 改开关/默认动作后要重新拉词库:开关一变,引擎行为就变了
  const saveCfg = async (key, value) => {
    try {
      await API.put('/api/admin/settings', { key, value: String(value) });
      setCfg((c) => ({ ...c, [key]: String(value) }));
      Toast.success('已保存');
    } catch (e) {
      Toast.error(apiErrMsg(e));
      load();
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      word: row.word,
      category: row.category || '其他',
      action: row.action || 'block',
      remark: row.remark || '',
      enabled: !!row.enabled,
    });
    setModalOpen(true);
  };

  const save = async () => {
    if (!form.word?.trim()) { Toast.error('请填写词条'); return; }
    setSaving(true);
    try {
      const payload = {
        word: form.word.trim(),
        category: form.category,
        action: form.action,
        remark: form.remark,
        enabled: form.enabled,
      };
      if (editing) await API.put(`/api/admin/risk/keywords/${editing.id}`, payload);
      else await API.post('/api/admin/risk/keywords', payload);
      Toast.success(editing ? '已保存' : '已添加,立即生效');
      setModalOpen(false);
      load();
    } catch (e) {
      Toast.error(apiErrMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = (row) => {
    Modal.confirm({
      title: '删除词条',
      content: `确定删除「${row.word}」吗?删除后立即生效。`,
      okType: 'danger',
      onOk: async () => {
        try {
          await API.delete(`/api/admin/risk/keywords/${row.id}`);
          Toast.success('已删除');
          load();
        } catch (e) {
          Toast.error(apiErrMsg(e));
        }
      },
    });
  };

  const bulkImport = async () => {
    const text = bulkText.trim();
    if (!text) { Toast.error('请粘贴要导入的词'); return; }
    setSaving(true);
    try {
      const r = await API.post('/api/admin/risk/keywords/bulk', {
        text, category: bulkCategory, action: 'block',
      });
      const d = r.data.data || {};
      Toast.success(`导入完成:新增 ${d.added} 个,跳过 ${d.skipped} 个(重复/过短)`);
      setBulkOpen(false);
      setBulkText('');
      load();
    } catch (e) {
      Toast.error(apiErrMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: '词条',
      dataIndex: 'word',
      render: (v) => <span style={{ fontWeight: 500 }}>{v}</span>,
    },
    {
      title: '分类',
      dataIndex: 'category',
      width: 110,
      render: (v) => <Tag color="orange" size="small">{v || '其他'}</Tag>,
    },
    {
      title: '处置动作',
      dataIndex: 'action',
      width: 110,
      render: (v) => (
        <Tag color={v === 'block' ? 'red' : 'blue'} size="small">
          {v === 'block' ? '拦截' : '仅记录'}
        </Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'enabled',
      width: 80,
      render: (v) => (v ? <Tag color="green" size="small">启用</Tag> : <Tag color="grey" size="small">停用</Tag>),
    },
    {
      title: '备注',
      dataIndex: 'remark',
      render: (v) => <span style={{ color: '#86909c', fontSize: 12 }}>{v || '—'}</span>,
    },
    {
      title: '操作',
      width: 140,
      render: (_v, row) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Button size="small" theme="borderless" onClick={() => openEdit(row)}>编辑</Button>
          <Button size="small" theme="borderless" type="danger" onClick={() => remove(row)}>删除</Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      {/* 顶部:风控总开关 + 默认动作 */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#18181b' }}>风控引擎</div>
            <div style={{ fontSize: 12, color: '#86909c', marginTop: 4, lineHeight: 1.7, maxWidth: 720 }}>
              《服务使用知情确认书》第 4 条承诺平台有权进行关键词过滤与违规内容拦截。<br />
              命中 <b>拦截</b> 的请求会被直接拒绝(不扣费、不打上游),命中记录会带 IP 与风控标记写入日志;<br />
              命中 <b>仅记录</b> 的请求正常放行,但命中信息仍会落库 —— 用于灰度观察误伤率。
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 13, color: '#4e5969' }}>总开关</span>
              <Switch
                checked={cfg.risk_filter_enabled !== '0'}
                onChange={(v) => saveCfg('risk_filter_enabled', v ? '1' : '0')}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 13, color: '#4e5969' }}>默认动作</span>
              <Select
                value={cfg.risk_filter_action}
                onChange={(v) => saveCfg('risk_filter_action', v)}
                style={{ width: 100 }}
                size="small"
                optionList={[
                  { value: 'block', label: '拦截' },
                  { value: 'review', label: '仅记录' },
                ]}
              />
            </div>
          </div>
        </div>
      </div>

      {/* 词库列表 */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#18181b' }}>
            违禁词库
            <span style={{ fontSize: 13, color: '#86909c', fontWeight: 400, marginLeft: 8 }}>
              {filtered.length === list.length
                ? `共 ${list.length} 条`
                : `筛出 ${filtered.length} / ${list.length} 条`}
            </span>
          </h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Input
              placeholder="搜索词条"
              size="small"
              value={filterWord}
              onChange={setFilterWord}
              style={{ width: 160 }}
              showClear
            />
            <Select
              placeholder="全部分类"
              size="small"
              value={filterCat}
              onChange={setFilterCat}
              style={{ width: 130 }}
              showClear
              optionList={categories.map((c) => ({ value: c.category, label: `${c.category} (${c.n})` }))}
            />
            <Button size="small" icon={<IconUpload />} onClick={() => setBulkOpen(true)}>批量导入</Button>
            <Button size="small" theme="solid" type="primary" icon={<IconPlus />} onClick={openNew}>添加词条</Button>
          </div>
        </div>

        <Table
          dataSource={pagedList}
          columns={columns}
          loading={loading}
          pagination={false}
          empty="还没有词条"
        />
        {filtered.length > TABLE_PAGE_SIZE && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <Pagination
              currentPage={tablePage}
              total={filtered.length}
              pageSize={TABLE_PAGE_SIZE}
              onPageChange={setTablePage}
              showTotal
            />
          </div>
        )}
      </div>

      {/* 单条编辑弹窗 */}
      <Modal
        title={editing ? '编辑词条' : '添加词条'}
        visible={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={save}
        confirmLoading={saving}
        okText="保存"
        cancelText="取消"
        width={460}
      >
        <div style={{ marginTop: 12 }}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, color: '#4e5969', marginBottom: 6 }}>词条</label>
            <Input
              value={form.word}
              onChange={(v) => setForm({ ...form, word: v })}
              placeholder="例如:赌博 / 彩票 / 代开发票"
              maxLength={40}
            />
            <div className="form-hint">
              匹配时忽略空格与标点,「赌 博」「赌-博」与「赌博」视为同一个词。单个字不生效(误伤面太大)。
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, color: '#4e5969', marginBottom: 6 }}>分类</label>
            <Select
              value={form.category}
              onChange={(v) => setForm({ ...form, category: v })}
              style={{ width: '100%' }}
              optionList={CATEGORY_OPTIONS.map((c) => ({ value: c, label: c }))}
              showSearch
            />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, color: '#4e5969', marginBottom: 6 }}>处置动作</label>
            <Select
              value={form.action}
              onChange={(v) => setForm({ ...form, action: v })}
              style={{ width: '100%' }}
              optionList={[
                { value: 'block', label: '拦截 —— 请求直接被拒(不扣费)' },
                { value: 'review', label: '仅记录 —— 正常放行,但记录命中信息' },
              ]}
            />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, color: '#4e5969', marginBottom: 6 }}>备注</label>
            <Input
              value={form.remark}
              onChange={(v) => setForm({ ...form, remark: v })}
              placeholder="选填,例如:用户投诉过的场景"
              maxLength={200}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Switch
              checked={form.enabled}
              onChange={(v) => setForm({ ...form, enabled: v })}
            />
            <span style={{ fontSize: 13, color: '#4e5969' }}>启用(停用后不参与匹配)</span>
          </div>
        </div>
      </Modal>

      {/* 批量导入弹窗 */}
      <Modal
        title="批量导入违禁词"
        visible={bulkOpen}
        onCancel={() => setBulkOpen(false)}
        onOk={bulkImport}
        confirmLoading={saving}
        okText="导入"
        cancelText="取消"
        width={560}
      >
        <div style={{ marginTop: 12 }}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, color: '#4e5969', marginBottom: 6 }}>分类</label>
            <Select
              value={bulkCategory}
              onChange={setBulkCategory}
              style={{ width: '100%' }}
              optionList={CATEGORY_OPTIONS.map((c) => ({ value: c, label: c }))}
              showSearch
            />
          </div>
          <label style={{ display: 'block', fontSize: 13, color: '#4e5969', marginBottom: 6 }}>词条(每行一个,也支持逗号分隔)</label>
          <Input.TextArea
            value={bulkText}
            onChange={setBulkText}
            placeholder={'赌博\n彩票\n代开发票'}
            autosize={{ minRows: 8, maxRows: 16 }}
            style={{ width: '100%' }}
          />
          <div className="form-hint">
            重复的词、过短的词会自动跳过;一次最多 2000 个。
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminRisk;
